import React, { useMemo } from 'react';

import { FlexBoxCol as Col, FlexBoxRow as Row } from '@r1-frontend/ui-react/components/layouts/flexBoxGrid';
import WideContainer from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';
import { useWebp } from '@r1-frontend/shared/hooks/useWebp';

import FilterMenu from '~/src/components/FilterMenu';
import { TSubmitFunc } from '~/src/components/RequestFormPhone';
import Advantages, { IAdvantage } from '~/src/features/domofon/components/Advantages';
import Features, { IFeature } from '~/src/features/domofon/components/Features';
import Headline from '~/src/features/domofon/components/Headline';
import Lesions, { ILesion } from '~/src/features/domofon/components/Lesions';

import * as ST from './styled';

interface IProps {
    advantagesItems: IAdvantage[],
    lesionsItems: ILesion[],
    featuresItems: IFeature[],
    hasDomofon: boolean,
    toggleDomofon: (flag: boolean) => void,
    submitRequest: TSubmitFunc,
    onStartFormFilling?: () => void,
}

export enum EMediaWidth {
    sDesktop = '(max-width: 1567px)',
    laptop = '(max-width: 1279px)',
    mobile = '(max-width: 767px)',
}

const BannerBlock: React.FC<IProps> = ({
    advantagesItems,
    lesionsItems,
    featuresItems,
    hasDomofon,
    toggleDomofon,
    submitRequest,
    onStartFormFilling,
}): JSX.Element => {
    const isMobile = useMatchMedia(EMediaWidth.mobile);
    const isDesktop = useMatchMedia(EMediaWidth.sDesktop);
    const isLaptop = useMatchMedia(EMediaWidth.laptop);
    const backgroundImg = useWebp()
        ? require('~/static/images/domofon-new/header_cover.webp')
        : require('~/static/images/domofon-new/header_cover.jpg');

    const menuItems = useMemo(() => ([
        {
            text: 'Есть домофон',
            action: () => {
                toggleDomofon(true);
            },
        },
        {
            text: 'Нет домофона',
            action: () => {
                toggleDomofon(false);
            },
        },
    ]), [toggleDomofon]);

    return (
        <ST.Wrapper backgroundImg={backgroundImg || ''} data-test='banner-block'>
            <WideContainer>
                <ST.Desc>
                    <Row>
                        <Col lg={7} md={12} sm={12} xs={12}>
                            <Headline
                                title={'Безопасность дома\nначинается с подъезда'}
                                text='Умный домофон от Дом.ру'
                            />
                            <Advantages data-test='banner-block-advantages' items={advantagesItems}/>
                        </Col>
                    </Row>
                </ST.Desc>
                <ST.Form>
                    <ST.SRequestFormPhone
                        templateVariant={'second'}
                        isMobile={isMobile}
                        isLoading={false}
                        submitRequest={submitRequest}
                        onStartFormFilling={onStartFormFilling}
                        isCheckInvertColor={true}
                        btnText='Оставить заявку'
                    />
                </ST.Form>
                <ST.Cards>
                    <ST.TabsTitle>В вашем доме установлен наш домофон?</ST.TabsTitle>
                    <FilterMenu
                        items={menuItems}
                        activeItem={hasDomofon ? 0 : 1}
                        isDifferentColorText={false}
                        colorTab={COLORS.ButtonAccent}
                    />
                    <ST.CardsTitle data-test='banner-block-cards-title'>
                        {hasDomofon
                            ? 'Возможности умного домофона:'
                            : 'В домах без умных домофонов'}
                    </ST.CardsTitle>
                    {
                        !hasDomofon
                            ? <Lesions
                                isDesktop={isDesktop}
                                isLaptop={isLaptop}
                                items={lesionsItems}
                                data-test='banner-block-lesions'
                            />
                            : <ST.FeaturesWrapper>
                                <Features
                                    items={featuresItems}
                                    showCarousel={isMobile}
                                    data-test='banner-block-features'
                                />
                            </ST.FeaturesWrapper>
                    }
                </ST.Cards>
            </WideContainer>
        </ST.Wrapper>
    );
};

export default BannerBlock;
